import { useMemo, useState } from "react";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { ProjectAccordion } from "../../components/features/time-allocation/components/project-accordion/project-accordion.tsx";
import {
  ProjectAllocationPanel,
  ProjectAllocationToolbar,
} from "../../components/features/time-allocation/components/project-allocation-panel/project-allocation-panel.tsx";
import { PeriodChangeConfirmModal } from "../../components/features/time-allocation/modals/period-change-confirm-modal.tsx";
import { CustomPeriodAllocationModal } from "../../components/features/time-allocation/modals/custom-period-allocation-modal.tsx";
import { ViewAllocationsModal } from "../../components/features/time-allocation/modals/view-allocations-modal.tsx";
import { SaveAssignedChangesConfirmModal } from "../../components/features/time-allocation/modals/save-assigned-changes-modal.tsx";
import { DiscardAssignedChangesConfirmModal } from "../../components/features/time-allocation/modals/discard-assigned-changes-modal.tsx";
import { useTimeAllocationPage } from "../../hooks/time-allocation/use-time-allocation-page.ts";
import { usePmMembers } from "../../hooks/time-allotment/use-pm-members.ts";
import { isGlobalAllocationType } from "../../utils/allocation-type.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { Button } from "../../components/ui/index.ts";
import { TailSpin } from "react-loader-spinner";

import styles from "./time-allocation-page.module.css";

const TimeAllocationPage = () => {
  const { projects, loading, error, refetch } = usePmMembers();

  const {
    openAccordionItem,
    periodByProjectId,
    periodChangeWarning,
    customDatePickerProjectId,
    viewAllocationsEmployee,
    projectsWithRemaining,
    pendingDiscard,
    requestPeriodChange,
    confirmPeriodChange,
    confirmCustomPeriodWithDates,
    cancelPeriodChange,
    cancelCustomDatePicker,
    setAssignedHours,
    openViewAllocations,
    closeViewAllocations,
    hasUnsavedAssignedForProject,
    submitProjectAllocations,
    requestToolbarDiscard,
    handleAccordionOpenChange,
    confirmPendingDiscard,
    cancelPendingDiscard,
  } = useTimeAllocationPage(projects);

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveSubmitting, setSaveSubmitting] = useState(false);

  const pendingCustomProject = useMemo(() => {
    if (!customDatePickerProjectId) return null;
    return (
      projectsWithRemaining.find((p) => p.id === customDatePickerProjectId) ??
      null
    );
  }, [customDatePickerProjectId, projectsWithRemaining]);

  const viewAllocationsLabels = useMemo(() => {
    if (!viewAllocationsEmployee) {
      return { projectName: "", employeeName: "" };
    }
    const proj = projectsWithRemaining.find(
      (p) => p.id === viewAllocationsEmployee.projectId,
    );
    const emp = proj?.employees.find(
      (e) => e.employeeId === viewAllocationsEmployee.employeeId,
    );
    return {
      projectName: proj?.name ?? "",
      employeeName: emp?.name ?? "",
    };
  }, [viewAllocationsEmployee, projectsWithRemaining]);

  const activeProjectName = useMemo(() => {
    if (!openAccordionItem) return "";
    return (
      projectsWithRemaining.find((p) => p.id === openAccordionItem)?.name ?? ""
    );
  }, [openAccordionItem, projectsWithRemaining]);

  const saveModalInactiveNames = useMemo(() => {
    if (!openAccordionItem) return [];
    const p = projectsWithRemaining.find((x) => x.id === openAccordionItem);
    if (!p) return [];
    return p.employees
      .filter(
        (e) =>
          e.assignedHours === 0 && !isGlobalAllocationType(e.allocationType),
      )
      .map((e) => e.name.trim())
      .filter(Boolean);
  }, [openAccordionItem, projectsWithRemaining]);

  const handleSaveClick = () => {
    setSaveModalOpen(true);
  };

  const handleSaveConfirm = async () => {
    if (openAccordionItem == null) return;
    setSaveSubmitting(true);
    try {
      await submitProjectAllocations(openAccordionItem);
      setSaveModalOpen(false);
      showSuccess("Allocations saved.");
      await refetch();
    } catch (e) {
      showError(
        e instanceof Error ? e.message : "Could not save allocations.",
      );
    } finally {
      setSaveSubmitting(false);
    }
  };

  const handleSaveCancel = () => {
    setSaveModalOpen(false);
  };

  const handleDiscardClick = () => {
    setSaveModalOpen(false);
    requestToolbarDiscard();
  };

  if (loading) {
    return (
      <PageShell>
        <Card padding="3xl">
          <div className={styles.loadingWrap} role="status" aria-live="polite">
            <TailSpin
              height="80"
              width="80"
              color="var(--primary-color)"
              visible={true}
            />
            <p className={styles.loadingText}>Loading time allocation…</p>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <Card padding="3xl">
          <div className={styles.errorWrap}>
            <p className={styles.errorText}>{error}</p>
            <Button type="button" variant="accent" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (projects.length === 0) {
    return (
      <PageShell>
        <Card padding="3xl">
          <EmptyStateMessage message="No Records Available" />
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Card padding="3xl">
        <div className={styles.pageInner}>
          <ProjectAccordion
            projects={projectsWithRemaining}
            openValue={openAccordionItem}
            onOpenChange={handleAccordionOpenChange}
            periodByProjectId={periodByProjectId}
            onPeriodChangeRequest={requestPeriodChange}
            renderHeaderToolbar={(project) =>
              hasUnsavedAssignedForProject(project.id) ? (
                <ProjectAllocationToolbar
                  onSaveClick={handleSaveClick}
                  onDiscardClick={handleDiscardClick}
                />
              ) : null
            }
            renderProjectContent={(project) => (
              <div className={styles.projectAccordianContent}>
                <ProjectAllocationPanel
                  project={project}
                  setAssignedHours={setAssignedHours}
                  openViewAllocations={openViewAllocations}
                />
              </div>
            )}
          />

          <PeriodChangeConfirmModal
            open={periodChangeWarning != null}
            nextPeriod={periodChangeWarning?.next ?? null}
            hasExistingAllocations={
              periodChangeWarning?.hasExistingAllocations ?? false
            }
            onConfirm={confirmPeriodChange}
            onCancel={cancelPeriodChange}
          />

          <CustomPeriodAllocationModal
            open={customDatePickerProjectId != null}
            hasProject={
              pendingCustomProject != null &&
              pendingCustomProject.employees.length > 0
            }
            onConfirmDates={(startIso, endIso) => {
              if (pendingCustomProject != null) {
                confirmCustomPeriodWithDates(
                  pendingCustomProject.id,
                  startIso,
                  endIso,
                );
              }
            }}
            onCancel={cancelCustomDatePicker}
          />

          <SaveAssignedChangesConfirmModal
            open={saveModalOpen}
            projectName={activeProjectName}
            inactiveEmployeeNames={saveModalInactiveNames}
            onConfirm={handleSaveConfirm}
            onCancel={handleSaveCancel}
            saving={saveSubmitting}
          />

          <DiscardAssignedChangesConfirmModal
            open={pendingDiscard != null}
            projectName={activeProjectName}
            variant={
              pendingDiscard?.kind === "navigation" ? "navigation" : "toolbar"
            }
            onConfirm={confirmPendingDiscard}
            onCancel={cancelPendingDiscard}
          />

          <ViewAllocationsModal
            open={viewAllocationsEmployee != null}
            employeeId={viewAllocationsEmployee?.employeeId ?? null}
            employeeName={viewAllocationsLabels.employeeName}
            projectName={viewAllocationsLabels.projectName}
            onClose={closeViewAllocations}
          />
        </div>
      </Card>
    </PageShell>
  );
};

export default TimeAllocationPage;
